import { useState, useRef, useEffect } from 'react';
import Portal from '../atoms/Portal';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export default function DatePicker({
  label,
  value,
  onChange,
  placeholder = 'Select date...',
  className = '',
  align = 'left',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const PICKER_WIDTH = 280;

  const initialDate = value ? new Date(value) : new Date(2026, 7, 1);
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth());

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();

  const toggleCalendar = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left:
          align === 'right'
            ? rect.right + window.scrollX - PICKER_WIDTH
            : rect.left + window.scrollX,
      });
    }
    setIsOpen(!isOpen);
  };

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleSelectDay = (day) => {
    const formattedMonth = String(currentMonth + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const selectedDateStr = `${currentYear}-${formattedMonth}-${formattedDay}`;
    onChange(selectedDateStr);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(e.target);

      if (isOutsideTrigger && isOutsidePopover) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className={`form-group ${className}`} ref={triggerRef}>
      {label && <label>{label}</label>}
      <div className="input-wrapper input-wrapper--has-prefix">
        <span className="input-icon-prefix">
          <Calendar size={16} />
        </span>
        <input
          type="text"
          readOnly
          className="input-field custom-datepicker-input"
          placeholder={placeholder}
          value={value || ''}
          onClick={toggleCalendar}
        />
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={popoverRef}
            className="portal-popover custom-datepicker"
            style={{
              top: coords.top,
              left: coords.left,
            }}
          >
            <div className="custom-datepicker__header">
              <button onClick={handlePrevMonth} className="custom-datepicker__nav-btn" aria-label="Previous Month">
                <ChevronLeft size={16} />
              </button>
              <div className="custom-datepicker__header-title">
                {monthNames[currentMonth]} {currentYear}
              </div>
              <button onClick={handleNextMonth} className="custom-datepicker__nav-btn" aria-label="Next Month">
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="custom-datepicker__weekdays">
              <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
            </div>

            <div className="custom-datepicker__days">
              {Array.from({ length: firstDayIndex }).map((_, i) => (
                <div key={`empty-${i}`} className="custom-datepicker__day custom-datepicker__day--empty" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const formattedMonth = String(currentMonth + 1).padStart(2, '0');
                const formattedDay = String(day).padStart(2, '0');
                const dateStr = `${currentYear}-${formattedMonth}-${formattedDay}`;
                const isSelected = value === dateStr;

                return (
                  <div
                    key={day}
                    className={`custom-datepicker__day ${isSelected ? 'custom-datepicker__day--selected' : ''}`}
                    onClick={() => handleSelectDay(day)}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
